import { split } from '@apollo/client';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { ApolloProvider } from '@apollo/react-common';
import { InMemoryCache, defaultDataIdFromObject } from 'apollo-cache-inmemory';
import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { setContext } from 'apollo-link-context';
import { onError } from 'apollo-link-error';
import { createUploadLink } from 'apollo-upload-client';
import React, { useState, useEffect } from 'react';
import { createNetworkStatusNotifier } from 'react-apollo-network-status';
import yn from 'yn';
import state from 'shared/state';
import storage from 'shared/storage';
export const { NetworkStatusNotifier, link: networkStatusNotifierLink } =
  createNetworkStatusNotifier();

// A GraphQL 404 sends the app to its ?404 screen, unless the operation says
// it shows its own not-found state: context: { handles404: true } (Forms).
export const redirectsOn404 = (message, operation) => {
  if (!message || message.code !== 404) return false;
  const context =
    operation && typeof operation.getContext === 'function'
      ? operation.getContext()
      : null;
  return !(context && context.handles404);
};

export const makeClient = async (secretKey) => {
  const cache = new InMemoryCache({
    dataIdFromObject: (object) =>
      // EventAssociatedTicket.id is the underlying ticket id, but quantity is
      // per-bundle — normalizing by id lets bundles that share a ticket
      // overwrite each other's quantity, so keep these embedded in the parent.
      object.__typename === 'EventAssociatedTicket'
        ? null
        : defaultDataIdFromObject(object),
  });

  const authLink = setContext((_, { headers }) => {
    let jwt = null;
    if (yn(process.env.debug)) {
      jwt = process.env.test_token.jwt;
    } else if (storage.get('promoter-user')) {
      jwt = storage.get('promoter-user').jwt;
    } else if (storage.get('user')) {
      jwt = storage.get('user').jwt;
    }
    return jwt
      ? {
          headers: {
            ...headers,

            authorization: `Bearer ${jwt}`,
            ...(secretKey ? { secret_key: secretKey } : {}),
          },
        }
      : {
          headers: {
            ...headers,
            ...(secretKey ? { Secret_key: secretKey } : {}),
          },
        };
  });

  const wsLink = new WebSocketLink({
    uri: `${process.env.REACT_APP_GRAPHQL_WS}`,
    options: {
      lazy: true,
      reconnect: true,
      connectionParams: async () => {
        const { jwt } = yn(process.env.debug)
          ? process.env.test_token
          : storage.get('user', {});
        return {
          Authorization: jwt ? `Bearer ${jwt}` : '',
        };
      },
    },
  });

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors)
      graphQLErrors.forEach((message) => {
        if (redirectsOn404(message, operation)) {
          if (window.location.search !== '?404')
            return (window.location.search = '?404');
        }

        if (message && message.code === 401) {
        }
        if (!message.field && (message.path || message.message))
          state.send(
            'error',
            message.message ||
              `There was a problem loading \`${message.path[0]}\` `
          );
      });
    if (networkError) console.warn(`[Network error]: ${networkError}`);

    if (
      networkError &&
      (networkError.statusCode === 401 || networkError.statusCode === 403)
    ) {
      storage.clear();
      return (window.location = `/?redirect=${window.location.pathname}`);
    }
  });

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    wsLink,
    ApolloLink.from([
      errorLink,
      authLink,
      networkStatusNotifierLink,
      createUploadLink({
        uri: process.env.REACT_APP_GRAPHQL,
        headers: {
          'apollo-require-preflight': 'true',
        },
      }),
    ])
  );

  return new ApolloClient({
    link: splitLink,
    name: 'dashboard',
    version: '1.0',
    cache,
    defaultOptions: {
      query: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
      },
    },
  });
};
export default ({ children, ...props }) => {
  const [client, setClient] = useState();

  useEffect(() => {
    const getClient = async () => {
      const client = await makeClient();
      setClient(client);
    };
    getClient();
  }, []);

  if (!client) return null;
  return (
    <ApolloProvider client={client}>
      {React.cloneElement(children, { ...props })}
    </ApolloProvider>
  );
};
